import { useEffect } from "react";
import { useFormContext } from "react-hook-form";

import { Box } from "ui/box";
import { Typography } from "ui/typography";

import DeliverableItem, {
  TEMPLATE_COLUMN_SX,
} from "components/modals/create-campaign-with-writer-modal/components/deliverable-item";
import { Button } from "ui/button";
import { DialogActions, DialogBackButton } from "ui/dialog";
import { useCampaignWithWriterContext } from "components/modals/create-campaign-with-writer-modal/context/campaign-with-writer-context";
import type { CampaignWriterDeliverable } from "components/modals/create-campaign-with-writer-modal/create-campaign-with-writer-modal.helper";
import type { CreateCampaignWithWriterFormData } from "components/modals/create-campaign-with-writer-modal/create-campaign-with-writer-modal.types";

import Icons from "assets";

type DeliverablesSelectionStepProps = {
  deliverables: CampaignWriterDeliverable[];
  isLoading: boolean;
};

export const DeliverablesSelectionStep = ({
  deliverables,
  isLoading,
}: DeliverablesSelectionStepProps) => {
  const { getValues, setValue } = useFormContext<CreateCampaignWithWriterFormData>();

  useEffect(() => {
    if (deliverables.length === 0) {
      return;
    }

    const currentSelections = getValues("deliverables") ?? [];
    const validIds = new Set(deliverables.map((deliverable) => deliverable.id));
    const hasValidSelections =
      currentSelections.length > 0 &&
      currentSelections.every((id) => validIds.has(id));

    if (!hasValidSelections) {
      setValue("deliverables", []);
    }
  }, [deliverables, getValues, setValue]);

  return (
    <div className="flex flex-col gap-4">
      <p className="text-gray-600">Please check campaign content and proceed.</p>

      <div className="flex flex-col gap-2">
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, pr: 1.5 }}>
          <Typography variant="body1" component="p" color="text.primary" sx={{ flex: 1 }}>
            Selected item(s)
          </Typography>
          {deliverables.length > 0 && (
            <Typography variant="body1" component="p" color="text.primary" sx={TEMPLATE_COLUMN_SX}>
              Creative templates
            </Typography>
          )}
        </Box>

        {isLoading ? (
          <div className="flex items-center gap-2 py-4 text-sm text-gray-500">
            <Icons.Spinner className="size-4 animate-spin" />
            Loading creatives...
          </div>
        ) : deliverables.length > 0 ? (
          <div className="flex flex-col gap-1 max-h-96 overflow-y-auto custom-scroll p-2.5 -m-2.5">
            {deliverables.map((deliverable) => (
              <DeliverableItem key={deliverable.id} deliverable={deliverable} />
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500">No creatives found in this template.</p>
        )}
      </div>
    </div>
  );
};

type DeliverablesSelectionStepActionsProps = {
  deliverables: CampaignWriterDeliverable[];
};

export const DeliverablesSelectionStepActions = ({
  deliverables,
}: DeliverablesSelectionStepActionsProps) => {
  const { watch } = useFormContext<CreateCampaignWithWriterFormData>();
  const { next: handleNext, back: handleBack } = useCampaignWithWriterContext();

  const deliverableSelections = watch("deliverables") ?? [];

  const hasSelectedDeliverables = (deliverableSelections ?? []).length > 0;

  const isContinueDisabled = deliverables.length === 0 || !hasSelectedDeliverables;

  return (
    <DialogActions
      type="steps"
      back={
          <DialogBackButton onClick={handleBack}>Back to brief</DialogBackButton>
        }
      primary={
          <Button size="large" disabled={isContinueDisabled} onClick={handleNext}>
            Continue with details
          </Button>
        }
    />
  );
};
